import { useState, useEffect, useCallback } from 'react';
import {
  ExtendedGovernanceTag,
  GovernanceTagParams,
  TagField,
  GovernanceTag,
  UseTagManagementProps,
  PendingTagUpdate,
  PendingSelectionUpdate,
} from '../../../types';
import { useGovernanceTags } from '../../../hooks/useGovernanceTags';
import { useRootStore } from 'app/contexts/RootStoreContext';
import GovernanceTagsApi from '../../../api/governanceTagsApi';
import styles from '../GovernanceTagsModal.module.scss';

export const useTagManagement = ({ resource, isOpen, onSaveTags, onDeleteTags, onClose }: UseTagManagementProps) => {
  const [tags, setTags] = useState<ExtendedGovernanceTag[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedTagIndices, setSelectedTagIndices] = useState<Set<number>>(new Set());
  const [initiallyHadTags, setInitiallyHadTags] = useState(false);

  const [pendingTagUpdate, setPendingTagUpdate] = useState<PendingTagUpdate | null>(null);
  const [pendingSelectionUpdate, setPendingSelectionUpdate] = useState<PendingSelectionUpdate | null>(null);

  const { addNewTagKey, addNewTagValue, addNewTagKeyValue } = useGovernanceTags();
  const { usersStore } = useRootStore();

  const groupTagsByUniqueName = (governanceTags: GovernanceTag[]): ExtendedGovernanceTag[] => {
    const groupedTags: Record<string, ExtendedGovernanceTag> = {};

    governanceTags.forEach((tag) => {
      const tagName = tag.name;

      if (!groupedTags[tagName]) {
        groupedTags[tagName] = {
          name: tagName,
          value: '',
          selectedValues: [],
        };
      }

      const tagGroup = groupedTags[tagName];
      if (tag.value && tag.value.trim() && tagGroup) {
        tagGroup.selectedValues?.push(tag.value);
        if (!tagGroup.value) {
          tagGroup.value = tag.value;
        }
      }
    });

    return Object.values(groupedTags);
  };

  useEffect(() => {
    if (pendingTagUpdate) {
      setTags(pendingTagUpdate.newTags);
      if (pendingTagUpdate.resetSelection) {
        setSelectAll(false);
      }
      setPendingTagUpdate(null);
    }
  }, [pendingTagUpdate]);

  useEffect(() => {
    if (pendingSelectionUpdate) {
      setSelectedTagIndices(pendingSelectionUpdate.selectedIndices);
      setSelectAll(pendingSelectionUpdate.selectAll);
      setPendingSelectionUpdate(null);
    }
  }, [pendingSelectionUpdate]);

  useEffect(() => {
    if (resource && isOpen) {
      const hadExistingTags = resource.governanceTags && resource.governanceTags.length > 0;

      let initialTags: ExtendedGovernanceTag[];

      if (hadExistingTags) {
        initialTags = groupTagsByUniqueName(resource.governanceTags!);
      } else {
        initialTags = [{ name: '', value: '', selectedValues: [] }];
      }

      setTags(initialTags);
      setSelectedTagIndices(new Set());
      setSelectAll(false);
      setInitiallyHadTags(hadExistingTags);
    }
  }, [resource, isOpen]);

  const handleAddTag = () => {
    const newTags = [...tags, { name: '', value: '', selectedValues: [] }];
    setPendingTagUpdate({ newTags, resetSelection: true });
    setPendingSelectionUpdate({ selectedIndices: new Set(), selectAll: false });
  };

  const fetchTagValues = useCallback(
    async (tagKey: string) => {
      try {
        const governanceTagsApi = new GovernanceTagsApi();
        const distinctValues = await governanceTagsApi.getDistinctTagValues({ tagKey });

        const tagValues = distinctValues[tagKey] || [];

        tagValues.forEach((value) => {
          addNewTagKeyValue(tagKey, value);
        });
      } catch {
        // Intentionally empty - continue with existing values if API fails
      }
    },
    [addNewTagKeyValue],
  );

  const handleTagChange = useCallback(
    (index: number, field: TagField, value: string | string[]) => {
      const newTags = [...tags];

      if (field === TagField.NAME) {
        newTags[index] = {
          ...newTags[index],
          [field]: typeof value === 'string' ? value : '',
          value: '',
          selectedValues: [],
        };

        if (typeof value === 'string' && value) {
          fetchTagValues(value);
        }
      } else if (field === TagField.VALUE) {
        let selectedValues: string[] = [];

        if (Array.isArray(value)) {
          selectedValues = [...value];
        } else if (value) {
          selectedValues = [value];
        }

        const mainValue = selectedValues.length > 0 ? selectedValues[0] : '';

        newTags[index] = {
          ...newTags[index],
          value: mainValue,
          selectedValues,
        };
      }

      setPendingTagUpdate({ newTags, resetSelection: true });
    },
    [tags, fetchTagValues],
  );

  const handleRemoveTag = (index: number) => {
    const newTags = [...tags];
    newTags.splice(index, 1);

    const newSelectedIndices = new Set(selectedTagIndices);
    newSelectedIndices.delete(index);
    const adjustedSelectedIndices = new Set<number>();
    newSelectedIndices.forEach((selectedIndex) => {
      if (selectedIndex > index) {
        adjustedSelectedIndices.add(selectedIndex - 1);
      } else {
        adjustedSelectedIndices.add(selectedIndex);
      }
    });

    const newSelectAll = adjustedSelectedIndices.size === newTags.length && newTags.length > 0;

    setPendingTagUpdate({ newTags });
    setPendingSelectionUpdate({ selectedIndices: adjustedSelectedIndices, selectAll: newSelectAll });
  };

  const handleAddNewTagKey = (value: string) => {
    if (value) {
      addNewTagKey(value);
    }
  };

  const handleAddNewTagValues = (value: string | string[]) => {
    const newValues: string[] = [];

    if (typeof value === 'string') {
      if (value) {
        newValues.push(value);
      }
    } else if (Array.isArray(value) && value.length > 0) {
      newValues.push(...value.filter((v) => v));
    }

    newValues.forEach((newValue) => {
      if (newValue) {
        addNewTagValue(newValue);
      }
    });
  };

  const handleSelectTag = (index: number, field: TagField, value: string | string[], isNew: boolean) => {
    handleTagChange(index, field, value);

    if (!isNew) return;

    switch (field) {
      case TagField.NAME: {
        const newKey = typeof value === 'string' ? value : '';
        handleAddNewTagKey(newKey);
        break;
      }
      case TagField.VALUE: {
        handleAddNewTagValues(value);
        break;
      }
    }
  };

  const handleCreateNewKey = (index: number) => {
    const tagInputs = document.querySelectorAll(`.${styles.tagInputs}`);
    const keyInput = tagInputs[index]?.querySelector('input');
    if (keyInput) {
      keyInput.focus();
    }
  };

  const handleCreateNewValue = (index: number) => {
    const tagInputs = document.querySelectorAll(`.${styles.tagInputs}`);
    const inputs = tagInputs[index]?.querySelectorAll('input');
    if (inputs?.length > 1) {
      inputs[1].focus();
    }
  };

  const handleSave = async () => {
    if (!resource) return;

    try {
      setIsLoading(true);
      const tagsToSave: GovernanceTag[] = [];

      tags.forEach(({ name, selectedValues }) => {
        if (name && selectedValues && selectedValues.length > 0) {
          selectedValues.forEach((value) => {
            if (value) {
              tagsToSave.push({ name, value });
            }
          });
        }
      });

      const params: GovernanceTagParams = {
        resourceId: resource.resourceId,
        name: resource.resourceName,
        createdBy: usersStore.currentDisplayedUserName || usersStore.getCurrUserName,
        governanceTags: tagsToSave,
      };

      params.governanceTags.forEach((tag) => {
        addNewTagKeyValue(tag.name, tag.value);
      });

      await onSaveTags(params);
      onClose();
    } catch {
      // Intentionally empty - error handling managed by parent component
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!resource) return;

    try {
      setIsLoading(true);
      await onDeleteTags(resource.resourceId);
      setPendingSelectionUpdate({ selectedIndices: new Set(), selectAll: false });
      onClose();
    } catch {
      // Intentionally empty - error handling managed by parent component
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    const newSelectedIndices = checked ? new Set(tags.map((_, index) => index)) : new Set<number>();

    setPendingSelectionUpdate({ selectedIndices: newSelectedIndices, selectAll: checked });
  };

  const handleTagSelect = (index: number, checked: boolean) => {
    const newSelectedIndices = new Set(selectedTagIndices);
    if (checked) {
      newSelectedIndices.add(index);
    } else {
      newSelectedIndices.delete(index);
    }

    const newSelectAll = newSelectedIndices.size === tags.length && tags.length > 0;

    setPendingSelectionUpdate({ selectedIndices: newSelectedIndices, selectAll: newSelectAll });
  };

  const hasEmptyTag = tags.some((tag) => !tag.name || !tag.selectedValues || tag.selectedValues.length === 0);
  const hasValidTags = tags.some((tag) => tag.name && tag.selectedValues && tag.selectedValues.length > 0);
  const hasRemovedAllExistingTags = initiallyHadTags && !hasValidTags;
  const canSave = hasValidTags || hasRemovedAllExistingTags;

  return {
    tags,
    isLoading,
    selectAll,
    selectedTagIndices,
    hasEmptyTag,
    canSave,
    hasRemovedAllExistingTags,
    handleAddTag,
    handleTagChange,
    handleRemoveTag,
    handleSelectTag,
    handleCreateNewKey,
    handleCreateNewValue,
    handleSave,
    handleDelete,
    handleSelectAll,
    handleTagSelect,
  };
};
